import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AutoComplete, Col, Radio, type RadioChangeEvent, Row } from 'antd-v5';
import cn from 'classnames';
import { clone, debounce, get, isEqual } from 'lodash';
import { MdCheckCircle, MdDeleteForever, MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Heading } from 'scouting-ui/lib';
import { v4 as uuidv4 } from 'uuid';

import type { CompletePersonProfile, Country, State } from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import {
  preciselyApi,
  useLazyGetPreciselyExpressCassAddressQuery,
} from '@modules/rtk/precisely-api';
import type { FormComponentProps } from '@shared/components/Form/Form.types';
import { intl } from '@shared/localization';

import { addressTypeIds, usAkelaCountryId } from '../../constants';
import {
  isLoadingDeleteEmailModalSel,
  setIsLoadingDeleteEmailModal,
  setShowDeleteAddressModal,
  showDeleteAddressModalSel,
} from '../../duck';
import isValueTruthy from '../../utils/isValueTruthy';
import Card from '../Card/Card';
import Form from '../Form';
import Input from '../Input';
import { DeleteEmailModal } from '../ProfileContactInfoCard/DeleteEmailModal';
import S from '../S';
import Select from '../Select';
import StateSelect from '../StateSelect';
import styles from './ProfileAddressCard.less';
import type {
  Address,
  CassAddress,
  FieldAddress,
} from './ProfileAddressCard.types';
import {
  autocompletePreferences,
  filterAddresses,
  getCurrentPrimaryAddressTypeId,
  getNewAddress,
  getZipInitialValue,
  mapPredictionToAddress,
} from './utils';

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;
const { Section: CardSection } = Card;
const { Item: FormItem } = Form;
const { Option } = AutoComplete;

const renderOption = (address: CassAddress, idx: number) => (
  <Option
    address={address}
    key={idx}
    text={address.AddressLine1}
    value={`${address.AddressLine1} ${idx}`}
  >
    {`${address.AddressLine1}, ${address.City}, ${address.State} ${address.PostalCode}`}
  </Option>
);

interface Props {
  countries: Country[];
  form: FormComponentProps['form'];
  isLoading?: boolean;
  isReadOnly?: boolean;
  isUnregisteredUser?: boolean;
  onConfirmDelete: (id: number) => void;
  states: State[];
  profile: CompletePersonProfile | object;
}

const ProfileAddressCard = ({
  countries,
  form,
  isLoading = false,
  isReadOnly,
  isUnregisteredUser = false,
  onConfirmDelete,
  profile,
  states,
}: Props) => {
  const dispatch = useDispatch();
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;
  const addresses = get(profile, 'addresses', []);
  const address3 = get(profile, 'profile.address3', '');
  const boysLife = get(profile, 'profile.boysLife', get(profile, 'boysLife'));
  const [adressTypeSelected, setAddressTypeSelected] = useState(
    getCurrentPrimaryAddressTypeId(addresses),
  );
  const [formAddresses, setFormAddresses] = useState<
    Array<Address | ReturnType<typeof getNewAddress>>
  >([]);
  const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);
  const showModal = useSelector(showDeleteAddressModalSel);
  const isDeleting = useSelector(isLoadingDeleteEmailModalSel);
  const countriesOptions = useMemo(
    () =>
      countries
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id = '-', name }) => (
          <Select.Option key={id} label={name} value={id}>
            {name}
          </Select.Option>
        )),
    [countries],
  );

  const [triggerGetCASS, { data }] =
    useLazyGetPreciselyExpressCassAddressQuery();

  // =========================================================================
  // Precisely X-Transaction-Id Session Management
  // =========================================================================
  const transactionIdRef = useRef<string | null>(null);

  const getOrCreateTransactionId = useCallback(() => {
    if (!transactionIdRef.current) {
      transactionIdRef.current = uuidv4();
    }
    return transactionIdRef.current;
  }, []);

  const resetTransactionId = useCallback(() => {
    transactionIdRef.current = null;
  }, []);

  // =========================================================================

  const addressPredictions = useMemo(
    () => data?.response.predictions?.map(mapPredictionToAddress) ?? [],
    [data],
  );

  const options = addressPredictions.map((predAddress, index) =>
    renderOption(predAddress, index),
  );

  const debouncedCASS = useMemo(
    () =>
      debounce((line1: string) => {
        const transactionId = getOrCreateTransactionId();

        triggerGetCASS({
          body: {
            address: {
              addressLines: [line1],
              country: 'USA',
            },
            preferences: autocompletePreferences,
          },
          transactionId,
        });
      }, 500),
    [getOrCreateTransactionId, triggerGetCASS],
  );

  const handleChangeSection = ({ target: { value } }: RadioChangeEvent) => {
    if (addressPredictions.length) {
      debouncedCASS.cancel();
      dispatch(preciselyApi.util.invalidateTags([tagTypes.CASSAutocomplete]));
    }

    resetTransactionId();
    setAddressTypeSelected(value);
  };

  const handleChangePrimary = (addressId: string) => () => {
    const addressesValue: Record<string, FieldAddress> =
      getFieldValue('addresses');
    const cloneFormAddresses = clone(addressesValue);

    const hasPrimaryAddress = Object.values(addressesValue).some(
      address => address?.isPrimary,
    );
    if (!hasPrimaryAddress) {
      const foundAddress = Object.entries(addressesValue).find(
        ([key]) => `id_${addressId}` == key,
      );

      if (!foundAddress) return;
      const [key, addressValues] = foundAddress;

      cloneFormAddresses[key] = { ...addressValues, isPrimary: true };

      setFieldsValue({ addresses: cloneFormAddresses });
      return;
    }

    const foundAddress = Object.entries(addressesValue).find(
      ([, value]) => value?.isPrimary && addressId !== value.id,
    );

    if (!foundAddress) return;

    const [key, addressValues] = foundAddress;

    cloneFormAddresses[key] = { ...addressValues, isPrimary: false };
    setFieldsValue({ addresses: cloneFormAddresses });
  };

  const showCheckmark = (typeId: number) => {
    const currentValues: Record<string, FieldAddress> =
      getFieldValue('addresses');

    if (currentValues) {
      const addressValues = Object.values(currentValues).find(
        value => value && value.typeId == String(typeId),
      );
      return addressValues ? addressValues?.isPrimary : false;
    }
    return false;
  };

  const isCountryRequired = (addressId: string) => {
    const currentValues: Record<string, FieldAddress> =
      getFieldValue('addresses')[`id_${addressId}`];

    const isPrimary = currentValues?.isPrimary;
    const address1Filled = !!currentValues.addressLine1;
    const stateFilled = !!currentValues.stateId;
    const zipFilled = !!currentValues.zip;
    if (!isPrimary && !address1Filled && !stateFilled && !zipFilled) {
      return false;
    }

    return true;
  };

  const isAddress1Required = (addressId: string) => {
    const currentValues: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];

    const isPrimary = currentValues?.isPrimary;
    const countrySelected = currentValues.countryId !== undefined;
    const stateFilled = !!currentValues.stateId;
    const zipFilled = !!currentValues.zip;
    if (!isPrimary && !countrySelected && !stateFilled && !zipFilled) {
      return false;
    }

    return true;
  };

  const isCityRequired = (addressId: string) => {
    const currentValues: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];

    const isPrimary = currentValues?.isPrimary;
    const countrySelected = currentValues.countryId !== undefined;
    const isUSCountry = currentValues.countryId == String(usAkelaCountryId);
    const address1Filled = !!currentValues.addressLine1;
    const stateFilled = !!currentValues.stateId;
    const zipFilled = !!currentValues.zip;
    if (countrySelected && !isUSCountry) {
      return false;
    }

    if (
      !isPrimary &&
      !countrySelected &&
      !address1Filled &&
      !zipFilled &&
      !stateFilled
    ) {
      return false;
    }

    return true;
  };

  const isStateRequired = (addressId: string) => {
    const currentValues: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];

    const isPrimary = currentValues?.isPrimary;
    const countrySelected = currentValues.countryId !== undefined;
    const isUSCountry = currentValues.countryId == String(usAkelaCountryId);
    const address1Filled = !!currentValues.addressLine1;
    const zipFilled = !!currentValues.zip;
    if (countrySelected && !isUSCountry) {
      return false;
    }

    if (!isPrimary && !countrySelected && !address1Filled && !zipFilled) {
      return false;
    }

    return true;
  };

  const isZipRequired = (addressId: string) => {
    const currentValues: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];

    const isPrimary = currentValues?.isPrimary;
    const countrySelected = currentValues.countryId !== undefined;
    const isUSCountry = currentValues.countryId == String(usAkelaCountryId);
    const address1Filled = !!currentValues.addressLine1;
    const stateFilled = !!currentValues.stateId;
    if (countrySelected && !isUSCountry) {
      return false;
    }

    if (!isPrimary && !countrySelected && !address1Filled && !stateFilled) {
      return false;
    }

    return true;
  };

  const isUSASelected = (addressId: string) => {
    const currentValues: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];
    const countrySelected = currentValues.countryId !== undefined;
    const isUSCountry = currentValues.countryId == String(usAkelaCountryId);

    return !countrySelected || isUSCountry;
  };

  const onClickDeleteAddress = (addressId: string) => () => {
    const indexOfAddress = formAddresses.findIndex(
      address => address.id === addressId,
    );
    const isNewAddress = addressId.startsWith('id_');

    if (isNewAddress && indexOfAddress !== -1) {
      const cloneFormAddresses = clone(formAddresses);
      cloneFormAddresses[indexOfAddress] = {
        ...getNewAddress(adressTypeSelected),
        id: `id_${addressId}`,
      };
      setFormAddresses(cloneFormAddresses);
    }

    if (!isNewAddress) {
      setIdToDelete(Number(addressId));
      dispatch(setShowDeleteAddressModal(true));
    }
  };

  const handleConfirmDelete = () => {
    if (idToDelete) {
      dispatch(setIsLoadingDeleteEmailModal(true));
      onConfirmDelete(idToDelete);
    }
  };

  const onCancelDelete = () => {
    if (!isDeleting) {
      dispatch(setShowDeleteAddressModal(false));
    }
  };

  const showDeleteBtn = (addressId: string, isOriginallyPrimary: boolean) => {
    if (isOriginallyPrimary) {
      return false;
    }

    const currentAddress: FieldAddress =
      getFieldValue('addresses')[`id_${addressId}`];

    if (currentAddress?.isPrimary) {
      return false;
    }

    const isNewAddress = addressId.startsWith('id_');
    const savedAddresses = formAddresses.filter(
      address => !address.id.startsWith('id_'),
    );

    if (
      isNewAddress &&
      (currentAddress.countryId ||
        currentAddress.addressLine1 ||
        currentAddress.addressLine2 ||
        currentAddress.city ||
        currentAddress.stateId ||
        currentAddress.zip)
    ) {
      return true;
    }

    if (
      !isNewAddress &&
      savedAddresses.length === 1 &&
      savedAddresses[0].id == addressId
    ) {
      return false;
    }

    if (!isNewAddress) {
      return true;
    }

    return false;
  };

  const handleChangeLine1 = (addressId: string) => (value: string) => {
    if (!isUSASelected(addressId)) {
      if (addressPredictions.length) {
        debouncedCASS.cancel();
        dispatch(preciselyApi.util.invalidateTags([tagTypes.CASSAutocomplete]));
      }
      resetTransactionId();
      return;
    }

    if (value && value.length > 4) {
      debouncedCASS(value);
    } else {
      // Clear/Reset transaction when character length drops to <= 4
      debouncedCASS.cancel();
      resetTransactionId();
    }
  };

  const handleSelectAddress =
    (addressId: string) =>
    (_selected: string, option: { address: CassAddress }) => {
      const { AddressLine1, City, PostalCode, State } = option.address;

      const dbState = states.find(({ short }) => short === State);
      const prefix = `addresses.id_${addressId}`;

      form.setFields({
        [`${prefix}.addressLine1`]: { value: AddressLine1, touched: true },
        [`${prefix}.addressLine2`]: { value: '', touched: true },
        [`${prefix}.city`]: { value: City, touched: true },
        [`${prefix}.stateId`]: {
          value: dbState ? String(dbState.id) : dbState,
          touched: true,
        },
        [`${prefix}.zip`]: { value: PostalCode, touched: true },
      });

      // Reset transaction session once address selection is completed
      resetTransactionId();
    };

  useEffect(() => {
    const filteredAddresses = filterAddresses(addresses);

    if (!isEqual(formAddresses, filteredAddresses)) {
      setFormAddresses(filteredAddresses);
    }
  }, [formAddresses, addresses]);

  return (
    <Card margin noPadding className={styles.card} loading={isLoading}>
      <CardSection
        className=""
        title={
          <Heading font="display-h5" level={2}>
            <FormattedMessage id="shared.addressInfo.title" />
          </Heading>
        }
      >
        {formAddresses.map(address => (
          <Fragment key={address.id}>
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className={cn({
                [styles.hidden]: address.typeId != String(adressTypeSelected),
              })}
            >
              <Col xs={24} lg={12}>
                {!isUnregisteredUser && (
                  <RadioGroup
                    defaultValue={addressTypeIds.HOME}
                    onChange={handleChangeSection}
                    buttonStyle="solid"
                    size="large"
                    value={adressTypeSelected}
                  >
                    <RadioButton value={addressTypeIds.HOME}>
                      <FormattedMessage id="shared.addressInfo.home" />
                      {showCheckmark(addressTypeIds.HOME) && (
                        <MdCheckCircle className={styles.primaryIcon} />
                      )}
                    </RadioButton>
                    <RadioButton value={addressTypeIds.BUSINESS}>
                      <FormattedMessage id="shared.addressInfo.business" />
                      {showCheckmark(addressTypeIds.BUSINESS) && (
                        <MdCheckCircle className={styles.primaryIcon} />
                      )}
                    </RadioButton>
                    <RadioButton value={addressTypeIds.VACATION}>
                      <FormattedMessage id="shared.addressInfo.vacation" />
                      {showCheckmark(addressTypeIds.VACATION) && (
                        <MdCheckCircle className={styles.primaryIcon} />
                      )}
                    </RadioButton>
                  </RadioGroup>
                )}
              </Col>
              <Col xs={24} lg={12}>
                <FormItem>
                  {getFieldDecorator(`addresses.id_${address.id}.isPrimary`, {
                    initialValue: isValueTruthy(address?.isPrimary),
                  })(
                    <Radio.Group onChange={handleChangePrimary(address.id)}>
                      <Radio
                        value={true}
                        className={styles.radio}
                        disabled={isReadOnly}
                      >
                        <FormattedMessage id="shared.addressInfo.isPrimary" />
                      </Radio>
                    </Radio.Group>,
                  )}
                </FormItem>
              </Col>
              <Col xs={24} lg={12}>
                <FormItem className={styles.formItemNormalWidth}>
                  {getFieldDecorator(`addresses.id_${address.id}.countryId`, {
                    initialValue: address.countryId,
                    rules: [
                      {
                        required: isCountryRequired(address.id),
                        message: intl.formatMessage({
                          id: 'shared.form.error.isRequired',
                        }),
                      },
                    ],
                  })(
                    <Select
                      disabled={isReadOnly}
                      floatingLabel
                      optionFilterProp="label"
                      placeholder={intl.formatMessage({
                        id: 'shared.addressInfo.country',
                      })}
                      rounded
                      showSearch
                      size="large"
                    >
                      {countriesOptions}
                    </Select>,
                  )}
                </FormItem>
              </Col>
              <Col xs={24} lg={12}>
                <FormItem
                  className={cn(
                    styles.formItemNormalWidth,
                    styles.autoCompleteFormItem,
                  )}
                >
                  {getFieldDecorator(
                    `addresses.id_${address.id}.addressLine1`,
                    {
                      initialValue: get(address, 'address1'),
                      rules: [
                        {
                          max: 255,
                          message: intl.formatMessage(
                            {
                              id: 'shared.maxCharacters',
                            },
                            { max: 255 },
                          ),
                        },
                        {
                          required: isAddress1Required(address.id),
                          message: intl.formatMessage({
                            id: 'shared.form.error.isRequired',
                          }),
                        },
                      ],
                    },
                  )(
                    <AutoComplete
                      className={styles.autoComplete}
                      disabled={isReadOnly}
                      onChange={handleChangeLine1(address.id)}
                      onSelect={handleSelectAddress(address.id)}
                      dataSource={options}
                      size="large"
                    >
                      <Input
                        size="large"
                        placeholder={intl.formatMessage({
                          id: 'shared.address.address1.placeholder',
                        })}
                        rounded
                        floatingLabel
                        disabled={isReadOnly}
                      />
                    </AutoComplete>,
                  )}
                </FormItem>
              </Col>
              <Col xs={24} lg={12}>
                <FormItem className={styles.formItemNormalWidth}>
                  {getFieldDecorator(
                    `addresses.id_${address.id}.addressLine2`,
                    {
                      initialValue: get(address, 'address2'),
                      rules: [
                        {
                          max: 255,
                          message: intl.formatMessage(
                            {
                              id: 'shared.maxCharacters',
                            },
                            { max: 255 },
                          ),
                        },
                      ],
                    },
                  )(
                    <Input
                      size="large"
                      placeholder={intl.formatMessage({
                        id: 'shared.address.address2.placeholder',
                      })}
                      rounded
                      floatingLabel
                      disabled={isReadOnly}
                    />,
                  )}
                </FormItem>
              </Col>
              <Col
                xs={24}
                lg={12}
                className={cn({
                  [styles.hidden]: isUSASelected(address.id),
                })}
              >
                <FormItem className={styles.formItemNormalWidth}>
                  {getFieldDecorator(
                    `addresses.id_${address.id}.addressLine3`,
                    {
                      initialValue: isUnregisteredUser
                        ? address3
                        : get(address, 'address3'),
                      rules: [
                        {
                          max: 255,
                          message: intl.formatMessage(
                            {
                              id: 'shared.maxCharacters',
                            },
                            { max: 255 },
                          ),
                        },
                      ],
                    },
                  )(
                    <Input
                      size="large"
                      placeholder={intl.formatMessage({
                        id: 'shared.address.address3.placeholder',
                      })}
                      rounded
                      floatingLabel
                      disabled={isReadOnly}
                    />,
                  )}
                </FormItem>
              </Col>
              <Col
                xs={24}
                lg={12}
                className={cn({
                  [styles.hidden]: !isUSASelected(address.id),
                })}
              >
                <FormItem className={styles.formItemNormalWidth}>
                  {getFieldDecorator(`addresses.id_${address.id}.city`, {
                    initialValue: get(address, 'city', ''),
                    rules: [
                      {
                        max: 50,
                        message: intl.formatMessage(
                          {
                            id: 'shared.maxCharacters',
                          },
                          { max: 50 },
                        ),
                      },
                      {
                        required: isCityRequired(address.id),
                        message: intl.formatMessage({
                          id: 'shared.form.error.isRequired',
                        }),
                      },
                    ],
                  })(
                    <Input
                      size="large"
                      placeholder={intl.formatMessage({
                        id: 'shared.address.city.placeholder',
                      })}
                      rounded
                      floatingLabel
                      disabled={isReadOnly}
                    />,
                  )}
                </FormItem>
              </Col>
              <Col
                xs={24}
                lg={12}
                className={cn({
                  [styles.hidden]: !isUSASelected(address.id),
                })}
              >
                <FormItem className={styles.formItemShortWidth}>
                  {getFieldDecorator(`addresses.id_${address.id}.stateId`, {
                    initialValue: get(address, 'stateId'),
                    rules: [
                      {
                        required: isStateRequired(address.id),
                        message: intl.formatMessage({
                          id: 'shared.form.error.isRequired',
                        }),
                      },
                    ],
                  })(
                    <StateSelect
                      states={states}
                      statesLoaded={true}
                      size="large"
                      rounded
                      floatingLabel
                      disabled={isReadOnly}
                    />,
                  )}
                </FormItem>
              </Col>
              <Col
                xs={24}
                lg={12}
                className={cn({
                  [styles.hidden]: !isUSASelected(address.id),
                })}
              >
                <FormItem className={styles.formItemShortWidth}>
                  {getFieldDecorator(`addresses.id_${address.id}.zip`, {
                    initialValue: getZipInitialValue(
                      get(address, 'zipCode', null),
                      get(address, 'zipCodeEx', null),
                    ),
                    rules: [
                      {
                        pattern: /^[0-9]{5}([- /]?[0-9]{4})?$/,
                        message: intl.formatMessage({
                          id: 'shared.addressInfo.zipError',
                        }),
                      },
                      {
                        required: isZipRequired(address.id),
                        message: intl.formatMessage({
                          id: 'shared.form.error.isRequired',
                        }),
                      },
                    ],
                  })(
                    <Input
                      maxLength={10}
                      size="large"
                      placeholder={intl.formatMessage({
                        id: 'shared.address.zipCode.label',
                      })}
                      rounded
                      floatingLabel
                      disabled={isReadOnly}
                    />,
                  )}
                </FormItem>
              </Col>
              <Col xs={24}>
                <FormItem className={styles.hidden}>
                  {getFieldDecorator(`addresses.id_${address.id}.typeId`, {
                    initialValue: address.typeId,
                  })(<Input size="large" rounded floatingLabel />)}
                </FormItem>
              </Col>
            </Row>
            <Row
              gutter={[24, 24]}
              className={cn({
                [styles.hidden]: address.typeId != String(adressTypeSelected),
              })}
            >
              {showDeleteBtn(address.id, isValueTruthy(address?.isPrimary)) && (
                <Col xs={24} lg={12}>
                  <Button
                    ghost
                    className={styles.deleteAddressBtn}
                    onClick={onClickDeleteAddress(address.id)}
                  >
                    <FormattedMessage id="shared.address.delete" />
                    <MdDeleteForever />
                  </Button>
                </Col>
              )}
            </Row>
            <Row
              gutter={[24, 24]}
              className={cn({
                [styles.hidden]: address.typeId != String(adressTypeSelected),
              })}
            >
              {isValueTruthy(boysLife) ? (
                <Col xs={24}>
                  <div className={styles.noticeContainer}>
                    <MdCheckCircle className={styles.checkIcon} />
                    <S size="4">
                      <FormattedMessage id="shared.addressInfo.scoutLife" />
                    </S>
                  </div>
                </Col>
              ) : (
                <Col xs={24}>
                  <div className={styles.noticeContainer}>
                    <S size="4">
                      <MdInfo className={styles.infoIcon} />
                      <FormattedMessage id="shared.addressInfo.noScoutLifeText" />
                      <a
                        className={styles.scoutLifeLink}
                        href="https://simplecirc.com/subscribe/scout-life/DIRPAS"
                        rel="noreferrer"
                        target="_blank"
                      >
                        <FormattedMessage id="shared.addressInfo.noScoutLifeLink" />
                      </a>
                    </S>
                  </div>
                </Col>
              )}
            </Row>
          </Fragment>
        ))}
      </CardSection>
      <DeleteEmailModal
        isLoading={isDeleting}
        onAccept={handleConfirmDelete}
        onCancel={onCancelDelete}
        titleId="deleteAddress"
        visible={showModal}
      />
    </Card>
  );
};

export default ProfileAddressCard;
